import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Stack,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";
import { InsertDriveFileOutlined as InsertDriveFileOutlinedIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { clearJobData, send_proof, proof_log_table, download_file, get_job_messages, get_sidebar_menu } from "../../../store/job/jobSlice";
import { isAuth } from "../../../helpers/cookies";
import { get_company_process_stage } from "../../../store/user/userSlice";


const SendProof = ({ is_SendProof_modal_open, handleSendProofModal, comp_file_list, job_id, job_detail }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { job_data, proof_log_data } = useSelector((state) => state.job);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userInteracted, setUserInteracted] = useState(false);

  const handleSendProofCloseModalChange = () => {
    formik.resetForm();
    setSelectedFiles([]);
    setUserInteracted(false);
    handleSendProofModal(false);
    dispatch(clearJobData());
  };

  const pdfFiles = [
    ...new Set(
      (comp_file_list || [])
        .filter((f) => /\.pdf$/i.test(f))
    )
  ];

  useEffect(() => {
    if (is_SendProof_modal_open && !userInteracted) {
      const preselected = pdfFiles.filter((fp) =>
        proof_log_data?.some(
          (p) => p.file_path === fp && [1, 2, 3].includes(p.sent_approved_files)
        )
      );
      setSelectedFiles(preselected);
    }
  }, [is_SendProof_modal_open, pdfFiles, proof_log_data, userInteracted]);


  const extractFilename = (path) => {
    if (!path) return "Unknown File";
    return path.split('/').pop();
  };
  const EMAIL_REGX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const validationSchema = yup.object().shape({
    Ref: yup.string().trim().required("Ref No is required."),
    customeremail: yup
      .string().trim()
      .matches(EMAIL_REGX, "Invalid email address.")
      .required("Email is required."),
    note: null,
    sent_to_cc: yup
      .string().trim()
      .nullable()
      .test(
        "valid-multiple-emails",
        "Use format: a@b.com,b@c.com",
        (value) => {
          if (!value) return true;
          const emails = value.split(",").map(e => e.trim());
          if (emails.some(e => e === "")) return false;
          return emails.every(e => EMAIL_REGX.test(e));
        }
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      Ref: job_data?.ref_No || "",
      customeremail: job_data?.customer_email || "",
      note: job_data?.note || "",
      sent_to_cc: "",

    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (selectedFiles.length === 0) {
          alert("Please select at least one file.");
          setSubmitting(false);
          return;
        }

        const payload = {
          ref_No: values.Ref,
          customeremail: values.customeremail,
          job_id: parseInt(job_id),
          note: values.note || null,
          sent_to_cc: values.sent_to_cc?.trim() || null,
          auth_user: parseInt(isAuth()?.user_id || isAuth()?.id),
          file_path: selectedFiles,
        };

        if (isNaN(payload.auth_user)) {
          alert("Authentication Error: Valid User ID not found. Please log out and log in again.");
          setSubmitting(false);
          return;
        }

        if (isNaN(payload.job_id)) {
          alert("Error: Invalid job ID.");
          setSubmitting(false);
          return;
        }

        const resultAction = await dispatch(send_proof(payload)).unwrap();
        let params = {
          company_id: job_detail.company_id,
          process_id: job_detail.process_id,
        };
        await dispatch(get_company_process_stage(params)).unwrap();
        await dispatch(get_sidebar_menu(job_detail.process_id));
        handleSendProofCloseModalChange(false);
        await dispatch(get_job_messages({ job_id }));
        await dispatch(proof_log_table({ job_id }));
      } catch (error) {
        console.error("Submission error:", error);
        alert(`Submission failed: ${typeof error === 'object' ? JSON.stringify(error) : error}`);
      } finally {
        setSubmitting(false);
      }
    }
  });

  const extractJobId = (fileKey) => {
    const parts = fileKey.split("/");
    const numericParts = parts.filter((p) => /^\d+$/.test(p));
    const jobId = numericParts.length > 0 ? numericParts[numericParts.length - 1] : null;
    return jobId ? parseInt(jobId, 10) : null;
  };

  const handleDownloadFile = async (fileKey) => {
    try {
      const jobId = extractJobId(fileKey);
      if (!jobId) return;

      const newTab = window.open("", "_blank");
      newTab.document.write(`
        <html>
          <head>
            <title>Loading...</title>
            <style>
              body {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                font-family: sans-serif;
                font-size: 18px;
                font-weight: 500;
              }
            </style>
          </head>
          <body>
            <div id="loading">Loading</div>
            <script>
              const el = document.getElementById("loading");
              let dots = 0;
              setInterval(() => {
                dots = (dots + 1) % 4;
                el.textContent = "Loading" + ".".repeat(dots);
              }, 500);
            </script>
          </body>
        </html>
      `);
      newTab.document.close();

      const action = await dispatch(
        download_file({ job_id: jobId, file_key: fileKey, d_type: "inline" })
      );

      if (download_file.fulfilled.match(action)) {
        const url = action.payload.data.url;

        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        newTab.location.href = blobUrl;

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      } else {
        console.error("Failed to Open file", action.payload);
        newTab.close();
      }
    } catch (err) {
      console.error("Open error:", err);
    }
  };


  return (
    <Dialog
      fullScreen={fullScreen}
      open={is_SendProof_modal_open}
      onClose={handleSendProofCloseModalChange}
      aria-labelledby="responsive-dialog-title"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <DialogTitle
        id="responsive-dialog-title"
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        Send Proof
        <IconButton size="medium" onClick={handleSendProofCloseModalChange}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          "& .MuiTextField-root,.MuiFormControl-root": { my: 1.2, width: { md: "40ch", xs: "100%" }, fontSize: "14px" },
        }}
      >
        <Stack sx={{ display: "flex", flexDirection: { md: "row", xs: "column" }, justifyContent: "space-between" }}>
          <TextField
            label="Ref #"
            variant="outlined"
            size="small"
            fullWidth
            required
            name="Ref"
            value={formik.values.Ref}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.Ref && Boolean(formik.errors.Ref)}
            helperText={formik.touched.Ref && formik.errors.Ref}
            InputProps={{
              readOnly: true,
            }}
          />
        </Stack>

        <Stack sx={{ display: "flex", flexDirection: { md: "row", xs: "column" }, justifyContent: "space-between" }}>
          <TextField
            label="Customer Email*"
            variant="outlined"
            size="small"
            fullWidth
            required
            name="customeremail"
            value={formik.values.customeremail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.customeremail && Boolean(formik.errors.customeremail)}
            helperText={formik.touched.customeremail && formik.errors.customeremail}
          />
        </Stack>
        <Stack sx={{ display: "flex", flexDirection: { md: "row", xs: "column" }, justifyContent: "space-between" }}>
          <TextField
            label="CC"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            name="sent_to_cc"
            value={formik.values.sent_to_cc}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.sent_to_cc && Boolean(formik.errors.sent_to_cc)}
            helperText={formik.touched.sent_to_cc && formik.errors.sent_to_cc}
          />
        </Stack>
        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Completed Files:
          </Typography>
          {pdfFiles.length > 0 ? (
            <List dense>
              {pdfFiles.map((filePath, index) => {
                const logEntry = proof_log_data?.find((p) => p.file_path === filePath);

                let customColor = "";
                if (logEntry) {
                  if (logEntry.sent_approved_files === 1) customColor = "#646262";
                  if (logEntry.sent_approved_files === 2) customColor = "#15922c";
                  if (logEntry.sent_approved_files === 3) customColor = "#fa1d00";
                }

                return (
                  <ListItem key={index} disableGutters>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 2,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedFiles.includes(filePath)}
                        onChange={(e) => {
                          setSelectedFiles((prev) =>
                            e.target.checked
                              ? [...prev, filePath]
                              : prev.filter((f) => f !== filePath)
                          );
                          setUserInteracted(true);
                        }}
                        sx={{
                          color: customColor || undefined,
                          "&.Mui-checked": {
                            color: customColor || "#1976d2",
                          },
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                        }}
                        onClick={() => handleDownloadFile(filePath)}
                      >
                        <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                        <ListItemText
                          primary={extractFilename(filePath)}
                          primaryTypographyProps={{
                            noWrap: true,
                            variant: "body2",
                            sx: {
                              maxWidth: "250px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No completed pdf files available.
            </Typography>
          )}
        </Box>
        <Stack sx={{ display: "flex", flexDirection: { md: "row", xs: "column" }, justifyContent: "space-between" }}>
          <TextField
            label="Note"
            variant="outlined"
            size="small"
            fullWidth
            name="note"
            multiline
            rows={3}
            value={formik.values.note}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.note && Boolean(formik.errors.note)}
            helperText={formik.touched.note && formik.errors.note}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: "8px 16px 24px 24px" }}>
        <Button variant="outlined" color="error" onClick={handleSendProofCloseModalChange}>
          Cancel
        </Button>
        <Button variant="contained" sx={{ backgroundColor: '#000000' }} type="submit">
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendProof;
